import { AppData } from '@wix/document-services-types';
import { FEATURE } from '@wix/deeplink-core';
import { Container, UnifiedWidget } from '../../../../types/unifiedComponents';
import { IntentInstallStore } from '../IntentInstallStore';
import type { ProgressBarManager } from '../ProgressBarManager';
import { findAsyncSequential } from '../helpers';
import { loadPanel } from './loadPanel';
import type { IntentModalsProps, WidgetsConfiguration } from './ui/types';
import { IntentInstallPanelsNames } from './ui/types';
import { PlatformContext } from '../../../../types/platformApi';
import { InstallationOriginType } from '@wix/editor-platform-sdk-types';

const token = '';

type PanelPromiseCallback<T> = (resolve: (data: T) => void) => void;

export class PanelsManager {
  private readonly originsWithoutMyBusiness = [
    InstallationOriginType.AI_ASSISTANT,
    InstallationOriginType.AI_SITE_CREATION,
    InstallationOriginType.SITE_GENERATION,
    InstallationOriginType.SITE_CREATION,
    InstallationOriginType.SILENT_INSTALL_SITE_CREATION,
  ];

  constructor(
    private context: PlatformContext,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {}

  private async openPlatformPanel<T extends IntentInstallPanelsNames>(
    panelName: T,
    props: IntentModalsProps[T],
  ) {
    this.context.platformApiMethods.editor.openPanel(
      {} as AppData,
      panelName,
      props,
      {
        // TODO: the commented out fields don't fit the type. Did i miss something?
        // name: panelName,
        // panelName,
        panelLoader: () => loadPanel(panelName),
        // componentRef: {},
        // openTime: Date.now(),
        // closeWithUserIntent: true,
        // frameType: 'platform',
      },
    );
  }

  private createPanelPromise<T>(cb: PanelPromiseCallback<T>) {
    return new Promise<T>((resolve) => {
      const resumeProgressBar = this.progressBarManager.pauseProgressBar();

      const wrappedResolve = (value: T) => {
        resolve(value);
        resumeProgressBar();
      };
      cb(wrappedResolve);
    });
  }

  public openPreIntentInstallModal() {
    const intentApp = this.intentInstallStore.getIntentApp();

    if (!intentApp) {
      return;
    }

    const otherAppsToInstall =
      this.intentInstallStore.getAppNamesToInstallExceptIntentApp();

    if (!otherAppsToInstall.length) {
      return;
    }

    const intentAppName = this.intentInstallStore.getIntentAppName()!;

    return this.createPanelPromise<void>((resolve) => {
      this.openPlatformPanel(IntentInstallPanelsNames.PreIntentInstallModal, {
        intentAppName,
        otherAppsToInstall,
        closePanel: () => {
          this.context.platformApiMethods.editor.closePanel(
            {} as AppData,
            '',
            IntentInstallPanelsNames.PreIntentInstallModal,
          );
          resolve();
        },
      });
    });
  }

  public openWidgetAppsIntentInstallModal(
    widgets: UnifiedWidget[],
    pages: Container[],
    appName: string,
  ) {
    return this.createPanelPromise<WidgetsConfiguration>((resolve) => {
      this.openPlatformPanel(
        IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
        {
          appName,
          widgets,
          pages,
          onSubmit: (widgetsConfig: WidgetsConfiguration) => {
            this.context.platformApiMethods.editor.closePanel(
              {} as AppData,
              '',
              IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
            );
            resolve(widgetsConfig);
          },
          onCancel: () => {
            this.context.platformApiMethods.editor.closePanel(
              {} as AppData,
              '',
              IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
            );
            resolve({});
          },
        },
      );
    });
  }

  public openPlatformFailedAlert() {
    const appsWithErrors = this.intentInstallStore.getAppsWithErrorsNames();
    if (!appsWithErrors.length) {
      return;
    }

    return this.context.platformApiMethods.editor.openPanel(
      {} as AppData,
      'savePublish.panels.common.failPanel',
      {
        titleKey: 'Notification_Box_App_Installation_Error_Title',
        description: 'Notification_Box_App_Installation_Error_Text',
        steps: [
          'Notification_Box_App_Installation_Error_Step1',
          'Notification_Box_App_Installation_Error_Step2',
        ],
        symbolName: 'serverError_NewWorkspace',
        helpMessage: '',
        helpLinkMessage: '',
        helpLink: '',
        translateArgs: {
          description: {
            appName: appsWithErrors.join(', '),
          },
        },
      },
      false,
    );
  }

  private async checkIfShouldOpenBusinessManager(appDefId: string) {
    return this.context.platformApiMethods.editor.deeplink.check(
      { appDefinitionId: appDefId } as AppData,
      'token',
      {
        type: FEATURE.myBusiness,
        params: [appDefId],
      },
    );
  }

  public async openBusinessPanel() {
    const isSilent = this.intentInstallStore.isSilent();

    const intentApp = this.intentInstallStore.getIntentApp();
    const appDefIds = isSilent
      ? this.intentInstallStore.getAppsToInstall()
      : intentApp?.appDefId
      ? [intentApp.appDefId]
      : this.intentInstallStore.getAppsToInstall();

    const selectedAppId = await findAsyncSequential(appDefIds, (appDefId) =>
      this.checkIfShouldOpenBusinessManager(appDefId),
    );

    if (!selectedAppId) {
      return;
    }

    const originType = this.intentInstallStore.getAppOriginType(selectedAppId);
    if (
      originType === undefined ||
      !this.originsWithoutMyBusiness.includes(originType)
    ) {
      this.context.editorPlatformPackagesBridge.ManageAppsPanelUIAPI?.openManageAppsPanel(
        { appDefinitionId: selectedAppId },
      );
    }
  }
}
